import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Star as StarFilledIcon,
  StarBorder as StarOutlineIcon,
  Dashboard as DashboardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import useApi from '../../hooks/useApi';
import { FONT_FAMILY_PRIMARY } from '../../constants/fonts';
import './FavoritesPage.css';

const FavoritesPage = () => {
  const navigate = useNavigate();
  const api = useApi();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboards', { params: { allTenant: true } });
      if (response.data?.success) {
        const dashboards = response.data.data?.dashboards ?? response.data.data ?? [];
        const list = Array.isArray(dashboards) ? dashboards : [];
        const favoriteDashboards = list.filter((d) => d.isFavorite);
        setFavorites(favoriteDashboards);
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const getId = (d) => d.dashboardId || d.DashboardId;
  const getName = (d) => d.dashboardName || d.DashboardName || 'Untitled Dashboard';

  const handleToggleFavorite = async (dashboard) => {
    const id = getId(dashboard);
    if (!id) return;
    try {
      const res = await api.put(`/dashboards/${id}/favorite`);
      if (res.data?.success && !res.data.data.isFavorite) {
        setFavorites((prev) => prev.filter((f) => getId(f) !== id));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontFamily: FONT_FAMILY_PRIMARY,
          fontWeight: 600,
          mb: 4,
        }}
      >
        Favorites
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : favorites.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: 'center' }}>
          <StarOutlineIcon sx={{ fontSize: 64, color: '#CCCCCC', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No favorites yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start favoriting dashboards to see them here
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {favorites.map((item) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={item.dashboardId}>
              <Card
                elevation={2}
                sx={{
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/dashboard/${item.jobId || getId(item)}`)}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 2,
                      }}
                    >
                      <DashboardIcon sx={{ fontSize: 40, color: '#000000' }} />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleToggleFavorite(item);
                        }}
                        sx={{ color: '#FFD700' }}
                      >
                        <StarFilledIcon />
                      </IconButton>
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {getName(item)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default FavoritesPage;
